export type SignedReceptionistContext = {
  slug: string;
  conversationId: string | null;
  historyToken: string | null;
  transcript: Array<{ role: "user" | "assistant"; content: string }>;
};

const memory = new Map<string, SignedReceptionistContext>();
const keyFor = (slug: string) => `ic-receptionist-booking-context:${slug}`;

export function parseSignedReceptionistContext(
  slug: string,
  value: unknown,
): SignedReceptionistContext | null {
  if (!slug || !value || typeof value !== "object") return null;
  const candidate = value as {
    conversationId?: unknown;
    historyToken?: unknown;
    messages?: unknown;
  };
  if (
    typeof candidate.historyToken !== "string" ||
    !candidate.historyToken ||
    !Array.isArray(candidate.messages) ||
    candidate.messages.length === 0 ||
    !candidate.messages.every((line) =>
      line &&
      typeof line === "object" &&
      ((line as { role?: unknown }).role === "user" || (line as { role?: unknown }).role === "assistant") &&
      typeof (line as { content?: unknown }).content === "string",
    )
  ) return null;
  return {
    slug,
    conversationId: typeof candidate.conversationId === "string" ? candidate.conversationId : null,
    historyToken: candidate.historyToken,
    transcript: candidate.messages as SignedReceptionistContext["transcript"],
  };
}

/** Only call after accepting a valid signed server chat response. */
export function saveReceptionistBookingContext(context: SignedReceptionistContext): void {
  const parsed = parseSignedReceptionistContext(context.slug, {
    conversationId: context.conversationId,
    historyToken: context.historyToken,
    messages: context.transcript,
  });
  if (!parsed) return;
  memory.set(context.slug, parsed);
  try {
    window.sessionStorage.setItem(keyFor(context.slug), JSON.stringify(parsed));
  } catch {
    // The live in-memory context remains available when storage is blocked.
  }
}

/** Retrieves context only from the slug-scoped handoff written by the widget. */
export function getReceptionistBookingContext(slug: string): SignedReceptionistContext | null {
  if (!slug) return null;
  const live = memory.get(slug);
  if (live) return live;
  try {
    const raw = window.sessionStorage.getItem(keyFor(slug));
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<SignedReceptionistContext>;
    if (
      value.slug !== slug ||
      typeof value.historyToken !== "string" ||
      !Array.isArray(value.transcript) ||
      !value.transcript.every((line) =>
        line && (line.role === "user" || line.role === "assistant") && typeof line.content === "string",
      )
    ) return null;
    return {
      slug,
      conversationId: typeof value.conversationId === "string" ? value.conversationId : null,
      historyToken: value.historyToken,
      transcript: value.transcript,
    };
  } catch {
    return null;
  }
}

export function clearReceptionistBookingContext(slug: string): void {
  memory.delete(slug);
  try { window.sessionStorage.removeItem(keyFor(slug)); } catch { /* best effort */ }
}
